// ============================================================================
// BENCHMARK CONFIGS - presets, config helpers, and the Karpathy loop prompt
// ============================================================================

import { TASK_MODEL_RECOMMENDATIONS, TASK_METADATA, listAllTasks, readModelFromRecommendation } from './model-selector';

// Flatten recommendation entries (string | {model, strongRecommendation, reason}) to plain model ids.
const FLAT_RECOMMENDATIONS = Object.fromEntries(
    Object.entries(TASK_MODEL_RECOMMENDATIONS).map(([k, v]) => [k, readModelFromRecommendation(v)])
);

// ─── Preset configurations ─────────────────────────────────────────────────

export const BENCHMARK_PRESETS = Object.freeze({
    // Use the global model for everything - baseline test
    GLOBAL_ONLY: {
        name: 'Global Model Only',
        description: 'Uses the global model for all tasks. No per-task overrides.',
        modelOverrides: {},
        promptOverrides: {},
    },

    // Use the tiered recommendations from model-selector.js
    TIERED_RECOMMENDED: {
        name: 'Tiered Recommended',
        description: 'Each task uses its tier-recommended model (Crusher/Worker/Reliable/Architect/Deep Thinker).',
        modelOverrides: { ...FLAT_RECOMMENDATIONS },
        promptOverrides: {},
    },

    // "Custom" is handled in the UI - user picks from their available models
    // via the AI Models page. No overrides here - the benchmark reads the
    // user's existing per-task config from localStorage.
});

// ─── Config builder helpers ─────────────────────────────────────────────────

export function createBenchmarkConfig({ name, projectId, goldStandardId, modelOverrides = {}, promptOverrides = {}, pipelineScope = 'full', tags = [] }) {
    return {
        name: name || `Benchmark ${new Date().toLocaleTimeString()}`,
        projectId,
        goldStandardId: goldStandardId || null,
        modelOverrides,
        promptOverrides,
        pipelineScope,
        tags,
    };
}

export function createFromPreset(presetKey, { projectId, goldStandardId, tags = [] }) {
    const preset = BENCHMARK_PRESETS[presetKey];
    if (!preset) throw new Error(`Unknown preset: ${presetKey}`);
    return createBenchmarkConfig({
        name: preset.name,
        projectId,
        goldStandardId,
        modelOverrides: { ...preset.modelOverrides },
        promptOverrides: { ...preset.promptOverrides },
        tags: [...tags, `preset:${presetKey.toLowerCase()}`],
    });
}

// ─── The Karpathy Loop "Research Director" Prompt ───────────────────────────
// This prompt is sent to the AI to propose the next experiment in the auto-loop.
// It receives the current config, recent run results, and the gold standard delta,
// then returns a single proposed change to try.

export const BENCHMARK_LOOP_PROMPT = `You are a construction quoting AI optimisation expert. Your job is to improve the accuracy of an AI pipeline that generates building remediation quotes by proposing targeted changes to model selection or prompt wording.

## OBJECTIVE

Maximise the composite accuracy score against a known-correct gold standard quote. The composite score weights ALL dimensions equally:
- **scopeAccuracy** (25%): Are the right items present?
- **priceAccuracy** (30%): Are the prices within ±5% of the gold standard?
- **scopeCompleteness** (20%): Are all gold standard items covered?
- **scopePrecision** (10%): Are there no unnecessary extra items?
- **descriptionQuality** (10%): Does the language match the builder's terminology?
- **inclusionAccuracy** (5%): Are the inclusions correct?

## CURRENT STATE

**Best composite score so far:** {CURRENT_BEST_SCORE}%
**Current weakest dimension:** {WEAKEST_DIMENSION} at {WEAKEST_SCORE}%

**Current model config:**
{CURRENT_MODEL_CONFIG}

**Current prompt summary (first 200 chars of each key prompt):**
{CURRENT_PROMPT_SUMMARY}

## GOLD STANDARD DELTA

Items where the AI output differs from the builder's actual sent quote:
{GOLD_STANDARD_DELTA}

## RECENT EXPERIMENTS

Last {RECENT_COUNT} runs (most recent first):
{RECENT_EXPERIMENTS}

## AVAILABLE MODELS (by cost)

{AVAILABLE_MODELS}

## AVAILABLE TASKS TO OVERRIDE

{AVAILABLE_TASKS}

## RULES

1. Propose exactly ONE change per experiment - don't stack multiple changes (can't attribute improvement)
2. Target the WEAKEST dimension - if price accuracy is lowest, propose a change likely to improve pricing
3. Don't repeat an experiment that was already tried and discarded (check RECENT EXPERIMENTS)
4. Consider cost: switching a cheap task (OCR, labeling) to Opus wastes money; switching risk-analysis or diagnosis to a stronger model is high-impact
5. For prompt changes: be specific about WHICH section to modify and provide the EXACT new text
6. For model changes: explain WHY this model would be better for this specific task
7. Think like an Australian builder: pricing must reflect real trade rates, scope must use builder terminology, inclusions must be practical

## OUTPUT

Return STRICT JSON:
{
  "change_type": "model" | "prompt" | "parameter",
  "task_or_prompt_id": "<the task name (for model) or prompt ID (for prompt)>",
  "new_value": "<the new model ID (for model) or the new prompt text (for prompt)>",
  "hypothesis": "<1-2 sentences explaining WHY this change should improve the weakest dimension>",
  "target_dimension": "<which metric this change aims to improve>",
  "estimated_impact": "low" | "medium" | "high"
}`;

// ─── Model pricing for cost estimation ──────────────────────────────────────

export const MODEL_PRICING = Object.freeze({
    'google/gemini-2.5-flash-lite':       { input: 0.075, output: 0.30 },
    'google/gemini-2.5-flash':            { input: 0.15,  output: 0.60 },
    'google/gemini-3.1-flash-lite':        { input: 0.08, output: 0.32 },
    'google/gemini-3.1-flash-lite-preview': { input: 0.08, output: 0.32 },
    // v5.12.8 fix: `google/gemini-3.1-flash-preview` doesn't exist on OpenRouter.
    // Gemini 3.1 only has flash-lite + pro. The middle-tier "flash-preview" lives
    // at the 3 generation, not 3.1.
    'google/gemini-3-flash-preview':      { input: 0.15,  output: 0.60 },
    'google/gemini-3-pro-preview':        { input: 1.25,  output: 10.00 },
    'google/gemini-3.1-pro-preview':      { input: 3.50,  output: 10.50 },
    'anthropic/claude-haiku-4.5':         { input: 0.80,  output: 4.00 },   // current Haiku GA on OpenRouter
    'anthropic/claude-sonnet-4.5':        { input: 3.00,  output: 15.00 },  // 1M-context BETA - retiring 2026-04-30
    'anthropic/claude-sonnet-4.6':        { input: 3.00,  output: 15.00 },  // 1M-context GA (since 2026-03-13)
    'anthropic/claude-opus-4.5':          { input: 15.00, output: 75.00 },  // legacy 200k
    // Opus 4.6 pricing dropped from $15/$75 to $5/$25 when 1M-GA dropped on 2026-03-13.
    // Source: openrouter.ai/anthropic/claude-opus-4.6/pricing (confirmed 2026-04-17).
    'anthropic/claude-opus-4.6':          { input: 5.00,  output: 25.00 },
    'anthropic/claude-opus-4.6-fast':     { input: 5.00,  output: 25.00 },
    'anthropic/claude-opus-4.7':          { input: 5.00,  output: 25.00 },  // 1M-context standard pricing
    'openai/gpt-5.4-pro':                { input: 10.00, output: 30.00 },
});

export function computeCostEstimate(telemetryEntries) {
    const breakdown = {};
    let totalUsd = 0;
    for (const entry of (telemetryEntries || [])) {
        const pricing = MODEL_PRICING[entry.model] || { input: 3.0, output: 15.0 };
        const costIn = ((entry.tokensIn || 0) / 1_000_000) * pricing.input;
        const costOut = ((entry.tokensOut || 0) / 1_000_000) * pricing.output;
        const cost = costIn + costOut;
        breakdown[entry.pipelineId] = (breakdown[entry.pipelineId] || 0) + cost;
        totalUsd += cost;
    }
    return { totalUsd: Math.round(totalUsd * 10000) / 10000, breakdown };
}

/**
 * Build the available models list for the loop prompt, sorted by cost.
 * Reads from MODEL_PRICING (always available) and enriches with the user's
 * cached model capabilities from localStorage if present.
 */
export function buildAvailableModelsList() {
    const entries = Object.entries(MODEL_PRICING)
        .map(([id, p]) => ({ id, inputCost: p.input, outputCost: p.output }))
        .sort((a, b) => a.inputCost - b.inputCost);

    return entries.map(m =>
        `- ${m.id} (~$${m.inputCost}/M input, ~$${m.outputCost}/M output)`
    ).join('\n');
}
